/**
 * InputPipeline — zero-copy input transport between the main thread and a Game Worker.
 *
 * A single-producer / single-consumer ring buffer inside a SharedArrayBuffer. The main
 * thread writes one fixed-size record per pointer/key event; the worker drains them
 * before each tick (and, where Atomics.waitAsync exists, the moment they land).
 *
 *   byte 0   Int32  header[0] writeIndex   (monotonic; slot = index % capacity)
 *   byte 4   Int32  header[1] readIndex
 *   byte 8   Int32  header[2] capacity
 *   byte 12  Int32  header[3] dropped      (events discarded because the ring was full)
 *   byte 16  Int32  header[4] layoutVersion
 *   byte 32  F64[4] sensor: beta, gamma, alpha, timestamp — latest gyro sample (no queue)
 *   byte 64  records, RECORD_BYTES each:
 *              +0  Int32 kind      +4  Int32 id
 *              +8  F32   x         +12 F32   y
 *              +16 F64   t         (hardware event time, epoch ms)
 *              +24 F64   wt        (time the record was written, epoch ms)
 *
 * Publish/consume ordering: the writer fills the slot, then Atomics.store()s the new
 * writeIndex (release); the reader Atomics.load()s writeIndex (acquire) before touching
 * slot data — so non-atomic Float64 reads never observe a torn record.
 *
 * All timestamps are `performance.timeOrigin + performance.now()`-style epoch ms so both
 * threads (which have different time origins) can compare them directly.
 */

export const INPUT_LAYOUT_VERSION = 1;
export const RECORD_BYTES = 32;
export const HEADER_BYTES = 32;
export const SENSOR_BYTES = 32;
export const RECORDS_OFFSET = HEADER_BYTES + SENSOR_BYTES; // 64
export const DEFAULT_CAPACITY = 512;

export const InputKind = {
  PointerDown: 1,
  PointerMove: 2,
  PointerUp: 3,
  PointerCancel: 4,
  Gyro: 5,
  KeyDown: 6,
  KeyUp: 7,
} as const;
export type InputKindValue = (typeof InputKind)[keyof typeof InputKind];

export interface InputRecord {
  kind: number;
  id: number;
  x: number;
  y: number;
  /** hardware/event timestamp, epoch ms */
  t: number;
  /** time the record was written, epoch ms */
  wt: number;
}

export function epochNow(): number {
  return performance.timeOrigin + performance.now();
}

export function inputBufferBytes(capacity = DEFAULT_CAPACITY): number {
  return RECORDS_OFFSET + capacity * RECORD_BYTES;
}

export function isSharedInputAvailable(): boolean {
  return (
    typeof SharedArrayBuffer !== 'undefined' &&
    typeof Atomics !== 'undefined' &&
    (typeof crossOriginIsolated === 'undefined' || crossOriginIsolated === true)
  );
}

export function createInputBuffer(capacity = DEFAULT_CAPACITY): SharedArrayBuffer {
  const sab = new SharedArrayBuffer(inputBufferBytes(capacity));
  const header = new Int32Array(sab, 0, HEADER_BYTES / 4);
  header[2] = capacity;
  header[4] = INPUT_LAYOUT_VERSION;
  return sab;
}

/** Views over either a SharedArrayBuffer or a plain ArrayBuffer with the same layout. */
class RingViews {
  readonly header: Int32Array;
  readonly sensor: Float64Array;
  readonly i32: Int32Array;
  readonly f32: Float32Array;
  readonly f64: Float64Array;
  readonly capacity: number;
  constructor(readonly buffer: SharedArrayBuffer | ArrayBuffer) {
    this.header = new Int32Array(buffer, 0, HEADER_BYTES / 4);
    this.sensor = new Float64Array(buffer, HEADER_BYTES, SENSOR_BYTES / 8);
    this.i32 = new Int32Array(buffer);
    this.f32 = new Float32Array(buffer);
    this.f64 = new Float64Array(buffer);
    this.capacity = this.header[2] || Math.floor((buffer.byteLength - RECORDS_OFFSET) / RECORD_BYTES);
  }
  writeSlot(slot: number, kind: number, id: number, x: number, y: number, t: number, wt: number) {
    const b = RECORDS_OFFSET + slot * RECORD_BYTES;
    this.i32[b >> 2] = kind;
    this.i32[(b >> 2) + 1] = id;
    this.f32[(b >> 2) + 2] = x;
    this.f32[(b >> 2) + 3] = y;
    this.f64[(b >> 3) + 2] = t;
    this.f64[(b >> 3) + 3] = wt;
  }
  readSlot(slot: number, out: InputRecord) {
    const b = RECORDS_OFFSET + slot * RECORD_BYTES;
    out.kind = this.i32[b >> 2];
    out.id = this.i32[(b >> 2) + 1];
    out.x = this.f32[(b >> 2) + 2];
    out.y = this.f32[(b >> 2) + 3];
    out.t = this.f64[(b >> 3) + 2];
    out.wt = this.f64[(b >> 3) + 3];
  }
}

/** Main-thread side. */
export class InputWriter {
  private views: RingViews;
  readonly shared: boolean;
  constructor(buffer: SharedArrayBuffer) {
    this.views = new RingViews(buffer);
    this.shared = typeof SharedArrayBuffer !== 'undefined' && buffer instanceof SharedArrayBuffer;
  }

  get buffer(): SharedArrayBuffer {
    return this.views.buffer as SharedArrayBuffer;
  }

  get dropped(): number {
    return Atomics.load(this.views.header, 3);
  }

  /** Returns false if the ring was full and the event was dropped. */
  push(kind: number, id: number, x: number, y: number, t: number = epochNow()): boolean {
    const h = this.views.header;
    const w = Atomics.load(h, 0);
    const r = Atomics.load(h, 1);
    if (w - r >= this.views.capacity) {
      Atomics.add(h, 3, 1);
      return false;
    }
    this.views.writeSlot(w % this.views.capacity, kind, id, x, y, t, epochNow());
    Atomics.store(h, 0, w + 1);
    Atomics.notify(h, 0);
    return true;
  }

  /** Latest-value sensor slot (gyro); overwrites, never queues. */
  setSensor(beta: number, gamma: number, alpha: number, t: number = epochNow()) {
    const s = this.views.sensor;
    s[0] = beta;
    s[1] = gamma;
    s[2] = alpha;
    s[3] = t;
  }
}

/** Worker side. */
export class InputReader {
  private views: RingViews;
  private scratch: InputRecord = { kind: 0, id: 0, x: 0, y: 0, t: 0, wt: 0 };
  private lastSensorT = 0;
  constructor(buffer: SharedArrayBuffer) {
    this.views = new RingViews(buffer);
  }

  get pendingCount(): number {
    const h = this.views.header;
    return Atomics.load(h, 0) - Atomics.load(h, 1);
  }

  get dropped(): number {
    return Atomics.load(this.views.header, 3);
  }

  /** Current write index — hand it to waitForInput() to sleep until it changes. */
  get writeIndex(): number {
    return Atomics.load(this.views.header, 0);
  }

  /**
   * Drain every pending record into `fn`, oldest first. The same scratch object is reused
   * for each call — copy fields out if you need to keep them.
   */
  drain(fn: (rec: InputRecord) => void): number {
    const h = this.views.header;
    const r = Atomics.load(h, 1);
    const w = Atomics.load(h, 0);
    for (let i = r; i < w; i++) {
      this.views.readSlot(i % this.views.capacity, this.scratch);
      fn(this.scratch);
    }
    if (w !== r) Atomics.store(h, 1, w);
    return w - r;
  }

  /** Returns the latest gyro sample if newer than the last one returned, else null. */
  takeSensor(): { beta: number; gamma: number; alpha: number; t: number } | null {
    const s = this.views.sensor;
    const t = s[3];
    if (!t || t === this.lastSensorT) return null;
    this.lastSensorT = t;
    return { beta: s[0], gamma: s[1], alpha: s[2], t };
  }

  /**
   * Resolves as soon as the write index moves past `seen` (Atomics.waitAsync), or after
   * `timeoutMs`. Returns null where waitAsync is unavailable (caller falls back to
   * per-frame draining).
   */
  waitForInput(seen: number, timeoutMs: number): Promise<'ok' | 'timed-out' | 'not-equal'> | null {
    const waitAsync = (Atomics as unknown as { waitAsync?: Function }).waitAsync;
    if (typeof waitAsync !== 'function') return null;
    const res = waitAsync.call(Atomics, this.views.header, 0, seen, timeoutMs) as {
      async: boolean;
      value: Promise<'ok' | 'timed-out'> | 'not-equal' | 'timed-out';
    };
    if (!res.async) return Promise.resolve(res.value as 'not-equal' | 'timed-out');
    return res.value as Promise<'ok' | 'timed-out'>;
  }
}

/**
 * postMessage fallback (no cross-origin isolation): pack records into a transferable
 * ArrayBuffer using the same slot layout so the worker decodes both paths identically.
 */
export function packRecords(records: InputRecord[]): ArrayBuffer {
  const buf = new ArrayBuffer(RECORDS_OFFSET + records.length * RECORD_BYTES);
  const views = new RingViews(buf);
  views.header[2] = records.length;
  views.header[4] = INPUT_LAYOUT_VERSION;
  for (let i = 0; i < records.length; i++) {
    const r = records[i];
    views.writeSlot(i, r.kind, r.id, r.x, r.y, r.t, r.wt);
  }
  views.header[0] = records.length;
  return buf;
}

export function unpackRecords(buf: ArrayBuffer, fn: (rec: InputRecord) => void): number {
  const views = new RingViews(buf);
  const n = views.header[0];
  const rec: InputRecord = { kind: 0, id: 0, x: 0, y: 0, t: 0, wt: 0 };
  for (let i = 0; i < n; i++) {
    views.readSlot(i, rec);
    fn(rec);
  }
  return n;
}
